// Libs
import { Box } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

// Components, Layouts, Pages
import { BaseToolbar, ConfirmModal, Loading } from '~/components/common';
import PageShell from '~/pages/common/pageShell/PageShell';
import { IntegrationPackage } from '~/components/module/integration';

// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { disconnectGoogleGmail, getAuthUrlGoogle } from '~/thunks/google/googleThunk';
import { getAuthUrlQuickBooks, disconnectQuickBooks } from '~/thunks/quickBooks/quickBooksThunk';
import { TConnectionStatus } from '~/utils/type/common';
import { GoogleConnectEnum, QuickBookConnectEnum } from '~/utils/enum/common';
import { userActions } from '~/thunks/user/userSlice';
import { MESSAGE_GOOGLE_CONNECTED, MESSAGE_QUICK_BOOK_CONNECTED } from '~/utils/constants/common';

// Styles, images, icons
import { icons } from '~/assets';
import styles from './Integration.module.scss';

const cx = classNames.bind(styles);

const Integration = () => {
  //#region Destructuring Props
  const { t } = useTranslation(['admin/integration']);
  const dispatch = useAppDispatch();
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  const { profile } = useAppSelector((state) => state.user);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirmDisconnectGoogle, setIsConfirmDisconnectGoogle] = useState<boolean>(false);
  const [isConfirmDisconnectQuickBooks, setIsConfirmDisconnectQuickBooks] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === MESSAGE_GOOGLE_CONNECTED) {
        dispatch(userActions.connectedGoogle());
      }

      if (event.data === MESSAGE_QUICK_BOOK_CONNECTED) {
        dispatch(userActions.connectedQuickBook());
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch]);
  //#endregion Implement Hook

  //#region Handle Function
  // Google
  const handleIntegrateGoogle = () => {
    if (profile?.google?.status === GoogleConnectEnum.CONNECTED) {
      setIsConfirmDisconnectGoogle(true);
      return;
    }

    handleGoogleConnect();
  };

  const handleGoogleDisconnect = () => {
    setIsLoading(true);
    dispatch(disconnectGoogleGmail())
      .unwrap()
      .then((res) => {
        setIsConfirmDisconnectGoogle(false);
        return dispatch(userActions.disconnectedGoogle());
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGoogleConnect = () => {
    dispatch(getAuthUrlGoogle())
      .unwrap()
      .then((res) => {
        if (!res) return;
        const { url } = res;
        window.open(url, '_blank');
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleShowConfirmDisconnectGoogle = () => {
    setIsConfirmDisconnectGoogle(!isConfirmDisconnectGoogle);
  };

  // QuickBooks
  const handleIntegrateQuickBooks = () => {
    if (profile?.quickBook?.status === QuickBookConnectEnum.CONNECTED) {
      setIsConfirmDisconnectQuickBooks(true);
      return;
    }

    handleQuickBooksConnect();
  };

  const handleQuickBooksConnect = () => {
    dispatch(getAuthUrlQuickBooks())
      .unwrap()
      .then((res) => {
        if (!res) return;
        window.open(res as string, '_blank');
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleQuickBooksDisconnect = () => {
    setIsLoading(true);
    dispatch(disconnectQuickBooks())
      .unwrap()
      .then(() => {
        setIsConfirmDisconnectQuickBooks(false);
        return dispatch(userActions.disconnectedQuickBook());
      })
      .catch((err) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleShowConfirmDisconnectQuickBooks = () => {
    setIsConfirmDisconnectQuickBooks(!isConfirmDisconnectQuickBooks);
  };
  //#endregion Handle Function

  return (
    <PageShell title={t('integration_title')}>
    <Box component={'div'} id='integrationPage' className={cx('container')}>
      <BaseToolbar />

      <Box component={'div'} className={cx('body')}>
        <IntegrationPackage
          status={profile?.google?.status?.toLowerCase() as TConnectionStatus}
          imgUrl={icons.integrationIconGoogleLogo}
          title={t('integration_google_title')}
          subtitle={t('integration_google_subtitle')}
          onCallback={handleIntegrateGoogle}
        />

        <IntegrationPackage
          status={(profile?.quickBook?.status?.toLowerCase() ?? 'disconnected') as TConnectionStatus}
          imgUrl={icons.integrationIconQuickBooksLogo}
          title={t('integration_quickbooks_title')}
          subtitle={t('integration_quickbooks_subtitle')}
          onCallback={handleIntegrateQuickBooks}
        />
      </Box>

      {isLoading && <Loading />}

      {isConfirmDisconnectGoogle && (
        <ConfirmModal
          icon={icons.integrationIconGoogleLogo}
          type='delete'
          title={t('common_confirm_disconnected_title', {
            ns: 'common/base',
          })}
          textConfirm={t('integration_btn_disconnect')}
          onClose={handleShowConfirmDisconnectGoogle}
          onConfirm={handleGoogleDisconnect}
        />
      )}

      {isConfirmDisconnectQuickBooks && (
        <ConfirmModal
          icon={icons.integrationIconQuickBooksLogo}
          type='delete'
          title={t('common_confirm_disconnected_title', {
            ns: 'common/base',
          })}
          textConfirm={t('integration_btn_disconnect')}
          onClose={handleShowConfirmDisconnectQuickBooks}
          onConfirm={handleQuickBooksDisconnect}
        />
      )}
    </Box>
    </PageShell>
  );
};

export default Integration;
